import React, { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Building2, Check, Copy, Loader2, Ticket, Upload } from 'lucide-react';
import api, { errMsg } from '@/lib/api';
import { useI18n } from '@/context/LanguageContext';
import { getDeviceId } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const copyText = async (text, successLabel, manualLabel) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    toast.success(successLabel);
  } catch (e) {
    toast.info(manualLabel);
  }
};

export const BuyCreditsDialog = ({ open, onOpenChange, info, onDone }) => {
  const { t } = useI18n();
  const [stage, setStage] = useState(1);
  const [pkgId, setPkgId] = useState(null);
  const [senderName, setSenderName] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const packages = info?.packages || [];
  const pkg = packages.find((p) => p.id === pkgId);
  const bank = info?.bank || {};
  const hasBank = info?.bank_enabled && (bank.iban || '').trim().length > 5;

  const reset = () => {
    setStage(1);
    setPkgId(null);
    setSenderName('');
    setReference('');
    setNote('');
    setReceipt(null);
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 200);
  };

  const uploadReceipt = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReceipt(data);
      toast.success(t('pay.receiptUploaded'));
    } catch (e) {
      toast.error(errMsg(e, t('pay.receiptFailed')));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (senderName.trim().length < 2) {
      toast.error(t('pay.senderRequired'));
      return;
    }
    setSending(true);
    try {
      await api.post('/payment-requests', {
        package_id: pkgId,
        method: 'bank',
        sender_name: senderName.trim(),
        reference: reference.trim(),
        note: note.trim(),
        receipt_file_id: receipt?.file_id || null,
        amount: pkg?.price ?? null,
      });
      toast.success(t('pay.submitted'));
      close(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e, t('pay.submitFailed')));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[88vh] overflow-y-auto border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">
            {stage === 1 ? t('pay.stage1Title') : stage === 2 ? t('pay.stage2Title') : t('pay.stage3Title')}
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--text-muted))]">
            {stage === 1 ? t('pay.stage1Desc') : stage === 2 ? t('pay.stage2Desc') : t('pay.stage3Desc')}
          </DialogDescription>
        </DialogHeader>

        {/* STAGE 1 - package */}
        {stage === 1 && (
          <div data-testid="buy-credits-amount-select" className="space-y-2.5">
            {packages.map((p) => {
              const sel = pkgId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`credit-package-${p.id}`}
                  onClick={() => setPkgId(p.id)}
                  className={[
                    'flex w-full items-center gap-3 rounded-[var(--radius-md)] border p-3.5 text-left transition-colors duration-150',
                    sel
                      ? 'border-[hsl(var(--brand-red))] bg-[hsl(var(--surface-2))]'
                      : 'border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] hover:border-[hsl(var(--border-strong))]',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'grid h-5 w-5 shrink-0 place-items-center rounded-full border',
                      sel
                        ? 'border-[hsl(var(--brand-red))] bg-[hsl(var(--brand-red))] text-white'
                        : 'border-[hsl(var(--border-strong))]',
                    ].join(' ')}
                  >
                    {sel && <Check className="h-3 w-3" />}
                  </span>
                  <span className="flex-1">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      {p.label}
                      {p.popular && (
                        <span className="rounded-full bg-[hsl(var(--brand-red))] px-2 py-0.5 text-[10px] font-bold text-white">
                          {t('pay.popular')}
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-[hsl(var(--text-muted))]">{p.desc}</span>
                  </span>
                  <span className="text-right">
                    <span className="block font-mono-alt text-sm font-bold text-[hsl(var(--brand-red))]">
                      {p.credits} {t('pay.creditsUnit')}
                    </span>
                    <span className="block font-mono-alt text-[11px] text-[hsl(var(--text-muted))]">
                      {p.price} {info?.currency || 'TL'}
                    </span>
                  </span>
                </button>
              );
            })}

            <Button
              disabled={!pkgId}
              onClick={() => setStage(2)}
              data-testid="buy-credits-next-button"
              className="mt-2 h-11 w-full bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
            >
              {t('common.next')} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* STAGE 2 - pay */}
        {stage === 2 && pkg && (
          <div className="space-y-4">
            <div className="rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{pkg.label}</span>
                <span className="font-mono-alt text-sm font-bold text-[hsl(var(--brand-red))]">
                  {pkg.credits} {t('pay.creditsUnit')} / {pkg.price} {info?.currency || 'TL'}
                </span>
              </div>
            </div>

            {hasBank && (
              <div className="space-y-2.5 rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] p-3.5">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Building2 className="h-4 w-4 text-[hsl(var(--brand-red))]" /> {t('pay.bankTitle')}
                </div>
                {bank.bank_name && (
                  <p className="text-xs text-[hsl(var(--text-muted))]">{bank.bank_name}</p>
                )}
                {bank.account_name && (
                  <p className="text-sm">
                    <span className="text-[hsl(var(--text-muted))]">{t('pay.receiver')}</span>
                    {bank.account_name}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <code
                    data-testid="bank-iban-text"
                    className="flex-1 truncate rounded-[8px] bg-[hsl(var(--surface))] px-2.5 py-2 font-mono-alt text-xs"
                  >
                    {bank.iban}
                  </code>
                  <Button
                    size="icon"
                    variant="secondary"
                    data-testid="copy-iban-button"
                    onClick={() =>
                      copyText(
                        bank.iban,
                        t('pay.ibanCopied'),
                        t('common.copyManually', { text: bank.iban })
                      )
                    }
                    className="h-9 w-9 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))]"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                {bank.note && <p className="text-xs text-[hsl(var(--text-muted))]">{bank.note}</p>}
              </div>
            )}

            {!hasBank && (
              <div
                data-testid="no-payment-method-warning"
                className="rounded-[var(--radius-md)] border border-[hsl(var(--brand-red))] bg-[hsl(var(--surface-2))] p-3.5 text-sm"
              >
                <p className="font-semibold">{t('pay.noMethodTitle')}</p>
                <p className="mt-1 text-[hsl(var(--text-muted))]">{t('pay.noMethodDesc')}</p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setStage(1)}
                className="h-11 flex-1 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]"
              >
                {t('common.back')}
              </Button>
              <Button
                onClick={() => setStage(3)}
                data-testid="i-paid-button"
                className="h-11 flex-1 bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
              >
                {t('pay.iPaid')}
              </Button>
            </div>
          </div>
        )}

        {/* STAGE 3 - notify */}
        {stage === 3 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sender">{t('pay.senderLabel')}</Label>
              <Input
                id="sender"
                data-testid="payment-sender-input"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder={t('pay.senderPlaceholder')}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ref">{t('pay.refLabel')}</Label>
              <Input
                id="ref"
                data-testid="payment-reference-input"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('pay.refPlaceholder')}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pnote">{t('pay.noteLabel')}</Label>
              <Textarea
                id="pnote"
                rows={3}
                data-testid="payment-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('pay.notePlaceholder')}
                className="resize-y bg-[hsl(var(--surface-2))]"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('pay.receiptLabel')}</Label>
              <label className="flex cursor-pointer items-center gap-3 rounded-[var(--radius-md)] border border-dashed border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-2))] px-3.5 py-3 transition-colors duration-150 hover:border-[hsl(var(--brand-red))]">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="payment-receipt-input"
                  onChange={(e) => {
                    if (e.target.files?.[0]) uploadReceipt(e.target.files[0]);
                    e.target.value = '';
                  }}
                />
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--brand-red))]" />
                ) : (
                  <Upload className="h-4 w-4 text-[hsl(var(--brand-red))]" />
                )}
                <span className="text-sm">
                  {receipt ? t('pay.receiptAdded') : t('pay.receiptChoose')}
                </span>
              </label>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setStage(2)}
                className="h-11 flex-1 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]"
              >
                {t('common.back')}
              </Button>
              <Button
                onClick={submit}
                disabled={sending}
                data-testid="payment-submit-button"
                className="h-11 flex-1 bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('pay.submit')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const RedeemCouponBox = ({ onDone }) => {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const redeem = async () => {
    if (!code.trim()) {
      toast.error(t('pay.couponEmpty'));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/coupons/redeem', {
        code: code.trim(),
        device_id: getDeviceId(),
      });
      toast.success(t('pay.couponSuccess', { count: data.added }));
      setCode('');
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e, t('pay.couponFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] p-4">
      <div className="flex items-center gap-2">
        <Ticket className="h-4 w-4 text-[hsl(var(--brand-red))]" />
        <h3 className="text-sm font-semibold">{t('pay.couponTitle')}</h3>
      </div>
      <p className="mt-1.5 text-xs text-[hsl(var(--text-muted))]">{t('pay.couponDesc')}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input
          data-testid="coupon-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') redeem();
          }}
          placeholder="SPK-XXXX-XXXX"
          className="bg-[hsl(var(--surface))] font-mono-alt uppercase"
        />
        <Button
          onClick={redeem}
          disabled={busy}
          data-testid="coupon-redeem-button"
          className="h-10 bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('pay.couponUse')}
        </Button>
      </div>
    </div>
  );
};

export const PaymentRequestList = ({ requests }) => {
  const { t, lang } = useI18n();
  if (!requests?.length) return null;

  const STATUS = {
    pending: { label: t('pay.statusPending'), cls: 'bg-[hsl(38,92%,50%)] text-black' },
    approved: { label: t('pay.statusApproved'), cls: 'bg-[hsl(142,70%,45%)] text-black' },
    rejected: { label: t('pay.statusRejected'), cls: 'bg-[hsl(var(--brand-red))] text-white' },
  };
  const locale = lang === 'en' ? 'en-US' : 'tr-TR';

  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold">{t('pay.requestsTitle')}</h3>
      <div
        data-testid="my-payment-requests"
        className="mt-3 divide-y divide-[hsl(var(--border-soft))] rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))]"
      >
        {requests.map((r) => {
          const st = STATUS[r.status] || STATUS.pending;
          return (
            <div key={r.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-xs font-medium">
                  {r.package_label} - {r.credits} {t('pay.creditsUnit')}
                  {r.amount ? ` / ${r.amount} TL` : ''}
                </p>
                <p className="font-mono-alt text-[10px] text-[hsl(var(--text-muted))]">
                  {new Date(r.created_at).toLocaleString(locale)}
                  {r.admin_note ? ` - ${r.admin_note}` : ''}
                </p>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>
                {st.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
